// src/components/Dashboard/Activity.js - FIXED TO USE DATEFILTERCONTEXT
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Button,
  Typography,
  Box,
  Chip,
  useTheme,
  alpha,
  Fade,
  Zoom,
  IconButton,
  Tooltip,
  Divider,
  Collapse,
  Stack,
  Alert
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ChevronRight as ChevronRightIcon,
  HistoryEdu as HistoryIcon,
  MoreVert as MoreVertIcon,
  Circle as CircleIcon,
  ExpandMore as ExpandMoreIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarMonth as CalendarIcon,
  FilterList as FilterListIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/helpers';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useDateFilter } from '../../contexts/DateFilterContext'; // Import DateFilterContext
import { useBudget } from '../../contexts/BudgetContext'; // Import BudgetContext
import { useCurrency } from '../../contexts/CurrencyContext';

export const DashboardActivity = ({ 
  maxTransactions = 10 
}) => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const { t } = useTranslation();
  const theme = useTheme();
  const themeContext = useThemeContext();
  const themeMode = themeContext?.themeMode || 'light';
  
  // Use DateFilterContext instead of props
  const { 
    selectedMonth, 
    selectedYear, 
    currentPeriodDisplay, 
    isCurrentMonth,
    filterTransactionsByMonth
  } = useDateFilter();
  
  // Get data from BudgetContext
  const { transactions = [], categories = [] } = useBudget();
  
  // Animation states
  const [showComponent, setShowComponent] = useState(false);
  const [expandedTransaction, setExpandedTransaction] = useState(null);

  // Filter transactions by selected date range (supports both month and range modes)
  const filteredTransactions = useMemo(() => {
    if (!transactions || !Array.isArray(transactions)) return [];
    
    return filterTransactionsByMonth(transactions)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, maxTransactions);
  }, [transactions, filterTransactionsByMonth, maxTransactions]);

  // Trigger animations on component mount
  useEffect(() => {
    setShowComponent(true);
  }, []);

  // Create a searchable map of categories for quick lookups
  const categoryMap = React.useMemo(() => {
    const map = new Map();
    
    // Map categories by both ID and lowercase name for flexible matching
    categories.forEach(category => {
      // Store by ID (or _id) for direct lookups
      if (category.id) map.set(category.id, category);
      if (category._id) map.set(category._id, category);
      
      // Store by lowercase name for case-insensitive lookups
      if (category.name) map.set(category.name.toLowerCase(), category);
    });
    
    return map;
  }, [categories]);

  // Find a category by ID (handles both MongoDB and localStorage formats)
  const getCategoryById = (categoryId) => {
    return categoryId ? categoryMap.get(categoryId) || null : null;
  };
  
  // Find a category by name (case-insensitive)
  const getCategoryByName = (name) => {
    return name ? categoryMap.get(name.toLowerCase()) || null : null;
  };
  
  // Get a category from a transaction using all available methods
  const getCategoryFromTransaction = (transaction) => {
    // For income, skip categories
    if (transaction.type === 'income') return null;
    
    let category = null;
    
    // Try to get category by ID first
    if (transaction.categoryId) {
      category = getCategoryById(transaction.categoryId);
    }
    
    // Next, try by direct category name if available
    if (!category && transaction.category) {
      category = getCategoryByName(transaction.category);
      
      // If category name doesn't match any existing category, create a placeholder
      if (!category && typeof transaction.category === 'string') {
        // Create a placeholder category with the provided name
        category = {
          name: transaction.category,
          // Generate a consistent color based on the category name
          color: stringToColor(transaction.category)
        };
      }
    }
    
    // As a fallback, look for keywords in the description that might match category names
    if (!category && transaction.description) {
      const description = transaction.description.toLowerCase();
      
      // Try to match any part of the description with category names
      for (const [key, value] of categoryMap.entries()) {
        // Only try to match by category names, not by IDs
        if (typeof key === 'string' && key.length > 3 && description.includes(key)) {
          category = value;
          break;
        }
      }
    }
    
    return category;
  };
  
  // Function to generate a consistent color from a string
  const stringToColor = (str) => {
    if (!str) return '#757575'; // Default gray
    
    // Generate a hash from the string
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Convert hash to RGB color
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xFF;
      color += ('00' + value.toString(16)).substr(-2);
    }
    
    return color;
  };

  // Toggle expanded transaction details
  const handleToggleDetails = (id) => {
    setExpandedTransaction(expandedTransaction === id ? null : id);
  };

  // Calculate transaction statistics for the selected period
  const transactionStats = useMemo(() => {
    const income = filteredTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    
    const expenses = filteredTransactions
      .filter(t => t.type === 'expense' || !t.type)
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    
    return {
      totalTransactions: filteredTransactions.length,
      incomeTransactions: filteredTransactions.filter(t => t.type === 'income').length,
      expenseTransactions: filteredTransactions.filter(t => t.type === 'expense' || !t.type).length,
      totalIncome: income,
      totalExpenses: expenses,
      netAmount: income - expenses
    };
  }, [filteredTransactions]);

  return (
    <Zoom in={showComponent} timeout={500} style={{ transitionDelay: '200ms' }}>
      <Card
        elevation={4}
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: theme.shadows[8],
          },
          background: themeMode === 'light' 
            ? 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(245,247,250,1) 100%)' 
            : 'linear-gradient(to bottom, rgba(30,30,46,1) 0%, rgba(25,25,39,1) 100%)',
          color: themeMode === 'light' ? 'rgba(0, 0, 0, 0.87)' : '#ffffff',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <CardHeader 
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: themeMode === 'light' 
              ? 'rgba(0, 0, 0, 0.08)' 
              : 'rgba(255, 255, 255, 0.08)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            background: themeMode === 'light'
              ? 'rgba(245, 247, 250, 0.5)'
              : 'rgba(15, 15, 25, 0.5)',
          }}
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <HistoryIcon 
                  sx={{ 
                    mr: 1.5, 
                    color: theme.palette.primary.main,
                    fontSize: 24 
                  }} 
                />
                <Typography 
                  variant="h6" 
                  fontWeight={600}
                  color={themeMode === 'light' ? 'inherit' : '#ffffff'}
                >
                  {currentPeriodDisplay} {t('dashboard.activity')}
                </Typography>
              </Box>
              
              {/* Period indicator and stats */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={<CalendarIcon />}
                  label={`${currentPeriodDisplay}${isCurrentMonth ? t('dashboard.summary.currentPeriodLabel') : ''}`}
                  size="small"
                  color="primary"
                  sx={{
                    height: 24,
                    borderRadius: 2,
                    fontWeight: 500,
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
                
                <Chip
                  icon={<TimelineIcon />}
                  label={`${transactionStats.totalTransactions} ${t('dashboard.transactions')}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 24,
                    borderRadius: 2,
                    fontWeight: 500,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
                
                {transactionStats.totalTransactions > 0 && (
                  <Chip
                    label={`Net: ${transactionStats.netAmount >= 0 ? '+' : ''}${formatCurrency(transactionStats.netAmount)}`}
                    size="small"
                    color={transactionStats.netAmount >= 0 ? 'success' : 'error'}
                    sx={{
                      height: 24,
                      borderRadius: 2,
                      fontWeight: 500,
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                )}
              </Box>
            </Box>
          }
          action={
            <Button 
              size="small" 
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              component={Link}
              to="/dashboard/transactions"
              sx={{
                color: theme.palette.primary.main,
                borderColor: alpha(theme.palette.primary.main, 0.5),
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              {t('dashboard.viewAll')}
            </Button>
          }
        />

        <List 
          sx={{ 
            p: 0, 
            flexGrow: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '4px',
              borderRadius: 2,
            },
            '&::-webkit-scrollbar-track': {
              background: themeMode === 'light' ? '#f1f1f1' : '#333'
            },
            '&::-webkit-scrollbar-thumb': {
              background: themeMode === 'light' ? '#cfcfcf' : '#555'
            }
          }}
        >
          {filteredTransactions.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              py: 6,
              px: 3,
              opacity: 0.7
            }}>
              <HistoryIcon 
                sx={{ 
                  fontSize: 60, 
                  mb: 2, 
                  color: themeMode === 'light' ? 'text.secondary' : 'rgba(255, 255, 255, 0.5)'
                }} 
              />
              
              <Typography 
                variant="body1" 
                fontSize="1.1rem"
                color={themeMode === 'light' ? 'text.primary' : '#ffffff'}
                align="center"
              >
                {t('dashboard.noTransactions')} {currentPeriodDisplay}
              </Typography>
              <Typography 
                variant="body2" 
                color={themeMode === 'light' ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)'}
                align="center"
                sx={{ mt: 1, maxWidth: 280 }}
              >
                {isCurrentMonth 
                  ? t('dashboard.startAddingTransactions')
                  : t('dashboard.noFinancialActivity')
                }
              </Typography>
              
              <Button
                variant="contained"
                size="small"
                component={Link}
                to="/dashboard/transactions"
                sx={{ 
                  mt: 3,
                  px: 3,
                  background: 'linear-gradient(90deg, #4b6cb7 0%, #182848 100%)',
                  borderRadius: 2,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                }}
              >
                {t('dashboard.addTransaction')}
              </Button>
            </Box>
          ) : (
            <Box>
              {/* Month summary for filtered view */}
              {transactionStats.totalTransactions > 0 && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    m: 2, 
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }}
                  icon={<FilterListIcon />}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {currentPeriodDisplay} {t('dashboard.summaryLabel')}: {' '}
                    <Box component="span" sx={{ color: 'success.main' }}>
                      +{formatCurrency(transactionStats.totalIncome)} {t('dashboard.income').toLowerCase()}
                    </Box>
                    {' · '}
                    <Box component="span" sx={{ color: 'error.main' }}>
                      -{formatCurrency(transactionStats.totalExpenses)} {t('dashboard.expenses').toLowerCase()}
                    </Box>
                    {' · '}
                    <Box component="span" sx={{ color: transactionStats.netAmount >= 0 ? 'success.main' : 'error.main' }}>
                      {transactionStats.netAmount >= 0 ? '+' : ''}{formatCurrency(transactionStats.netAmount)} {t('dashboard.net').toLowerCase()}
                    </Box>
                  </Typography>
                </Alert>
              )}
              
              {filteredTransactions.map((transaction, index) => {
                // Ensure salary transactions are always income
                if (transaction.description && 
                    transaction.description.toLowerCase().includes('salary')) {
                  transaction.type = 'income';
                }
                
                // Get category for the transaction
                const category = getCategoryFromTransaction(transaction);
                
                // Create a unique ID for this transaction to use with animations and expanded state
                const transactionId = transaction.id || `transaction-${index}`;
                
                // Format the transaction date
                const transactionDate = new Date(transaction.date);
                const formattedDate = transactionDate.toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: transactionDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                });
                
                return (
                  <React.Fragment key={`${transactionId}-${themeMode}-${selectedMonth}-${selectedYear}`}>
                    <Fade in={showComponent} timeout={500} style={{ transitionDelay: `${150 * (index % 10)}ms` }}>
                      <ListItem 
                        sx={{ 
                          px: 3,
                          py: 2,
                          position: 'relative',
                          transition: 'all 0.2s ease',
                          borderBottom: '1px solid',
                          borderColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
                            '& .transaction-action': {
                              opacity: 1
                            }
                          },
                          '&:last-child': { 
                            borderBottom: 'none' 
                          }
                        }}
                        onClick={() => handleToggleDetails(transactionId)}
                      >
                        {/* Date Indicator */}
                        <Box 
                          sx={{ 
                            minWidth: 42,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mr: 2
                          }}
                        >
                          <Typography 
                            variant="caption"
                            sx={{ 
                              display: 'block',
                              fontWeight: 600,
                              color: themeMode === 'light' ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)'
                            }}
                          >
                            {formattedDate.split(' ')[0]}
                          </Typography>
                          <Typography 
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              lineHeight: 1.2,
                              color: transaction.type === 'income' 
                                ? 'success.main'
                                : themeMode === 'light' ? 'text.primary' : '#fff'
                            }}
                          >
                            {formattedDate.split(' ')[1]}
                          </Typography>
                        </Box>
                        
                        {/* Transaction Icon */}
                        <Avatar 
                          sx={{ 
                            width: 42, 
                            height: 42,
                            mr: 2,
                            background: transaction.type === 'income'
                              ? `linear-gradient(45deg, ${alpha(theme.palette.success.light, 0.8)}, ${alpha(theme.palette.success.main, 0.7)})`
                              : category 
                                ? `linear-gradient(45deg, ${alpha(category.color, 0.8)}, ${alpha(category.color, 0.6)})`
                                : `linear-gradient(45deg, ${alpha(theme.palette.error.light, 0.8)}, ${alpha(theme.palette.error.main, 0.7)})`,
                            boxShadow: transaction.type === 'income'
                              ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
                              : category
                                ? `0 4px 12px ${alpha(category.color, 0.3)}`
                                : `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                            color: '#fff'
                          }}
                        >
                          {transaction.type === 'income' ? (
                            <TrendingUpIcon />
                          ) : (
                            category && category.icon ? category.icon : <TrendingDownIcon />
                          )}
                        </Avatar>
                        
                        {/* Transaction Details */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography 
                                variant="body1" 
                                fontWeight={600}
                                color={themeMode === 'light' ? 'text.primary' : '#ffffff'}
                                sx={{ 
                                  mb: 0.5,
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {transaction.description}
                              </Typography>
                              
                              <Stack direction="row" spacing={1} alignItems="center">
                                {/* Category Chip */}
                                <Chip
                                  size="small"
                                  label={
                                    transaction.type === 'income' ? t('dashboard.income') :
                                    category ? category.name : 
                                    transaction.category ? transaction.category : 
                                    t('dashboard.uncategorized')
                                  }
                                  icon={transaction.type === 'income' ? undefined : 
                                    <CircleIcon sx={{ 
                                      fontSize: '10px !important', 
                                      ml: '4px',
                                      color: category 
                                        ? category.color 
                                        : themeMode === 'light' ? 'grey.700' : 'grey.300'
                                    }} />
                                  }
                                  sx={{
                                    height: 22,
                                    borderRadius: '12px',
                                    fontWeight: 500,
                                    backgroundColor: transaction.type === 'income'
                                      ? alpha(theme.palette.success.main, 0.1)
                                      : category
                                        ? alpha(category.color, 0.1)
                                        : themeMode === 'light' ? 'grey.100' : 'grey.800',
                                    color: transaction.type === 'income'
                                      ? theme.palette.success.dark
                                      : category
                                        ? category.color
                                        : themeMode === 'light' ? 'grey.700' : 'grey.300',
                                    '& .MuiChip-label': { 
                                      px: transaction.type === 'income' ? 1.5 : 0.75, 
                                      paddingLeft: transaction.type === 'income' ? undefined : 0, 
                                      fontSize: 11,
                                      fontWeight: 600
                                    }
                                  }}
                                />
                                
                                {/* Expand indicator */}
                                <Tooltip title={expandedTransaction === transactionId ? t('dashboard.hideDetails') : t('dashboard.showDetails')}>
                                  <ExpandMoreIcon 
                                    fontSize="small" 
                                    sx={{ 
                                      color: theme.palette.action.active,
                                      opacity: 0.6,
                                      transform: expandedTransaction === transactionId ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.2s ease'
                                    }} 
                                  />
                                </Tooltip>
                              </Stack>
                            </Box>
                            
                            {/* Amount */}
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: transaction.type === 'income' ? 'success.main' : 'error.main',
                                ml: 1,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {transaction.type === 'income' ? '+' : '-'}
                              {formatCurrency(transaction.amount)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Action buttons - visible on hover */}
                        <Box 
                          className="transaction-action"
                          sx={{ 
                            position: 'absolute',
                            top: 10,
                            right: 16,
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            display: 'flex'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title={t('dashboard.share')}>
                            <IconButton size="small">
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('dashboard.moreOptions')}>
                            <IconButton size="small">
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    </Fade>
                    
                    {/* Expanded transaction details */}
                    <Collapse in={expandedTransaction === transactionId} timeout="auto" unmountOnExit>
                      <Box 
                        sx={{ 
                          p: 2,
                          pl: 12, // Align with main content
                          bgcolor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
                          borderBottom: '1px solid',
                          borderColor: themeMode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('dashboard.transactionId')}
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace">
                              {transactionId}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('dashboard.dateTime')}
                            </Typography>
                            <Typography variant="body2">
                              {new Date(transaction.date).toLocaleString()}
                            </Typography>
                          </Box>
                          
                          {transaction.paymentMethod && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                {t('dashboard.paymentMethod')}
                              </Typography>
                              <Typography variant="body2">
                                {transaction.paymentMethod}
                              </Typography>
                            </Box>
                          )}
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('dashboard.period')}
                            </Typography>
                            <Typography variant="body2">
                              {currentPeriodDisplay}
                            </Typography>
                          </Box>
                          
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            sx={{ 
                              alignSelf: 'flex-end',
                              mt: 1,
                              color: theme.palette.primary.main
                            }}
                          >
                            {t('dashboard.downloadReceipt')}
                          </Button>
                        </Stack>
                      </Box>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </Box>
          )}
        </List>
      </Card>
    </Zoom>
  );
};

export default DashboardActivity;